import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Photo, PaginationInfo } from '../../types';
import PhotoCard from '../shared/PhotoCard';
import { ChevronLeft, ChevronRight, Download, Eye, Grid } from 'lucide-react';

interface PhotoViewerProps {
  photos: Photo[];
  selectedPhotos: number[];
  onPhotoSelect: (photo: Photo) => void;
  onSelectAll: () => void;
  onDownload: () => void;
  pagination?: PaginationInfo;
  currentPage: number;
  onPageChange: (page: number) => void;
  isDownloading: boolean;
}

export default function PhotoViewer({
  photos,
  selectedPhotos,
  onPhotoSelect,
  onSelectAll,
  onDownload,
  pagination,
  currentPage,
  onPageChange,
  isDownloading,
}: PhotoViewerProps) {
  const [lightboxPhoto, setLightboxPhoto] = useState<Photo | null>(null);

  // Prevent body scrolling and handle keyboard when lightbox is open
  useEffect(() => {
    if (lightboxPhoto) {
      // Store current scroll position
      const scrollY = window.scrollY;
      
      // Prevent body scrolling
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      // Handle ESC key
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          closeLightbox();
        }
      };
      
      document.addEventListener('keydown', handleKeyDown);
      
      return () => {
        // Restore scroll position
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
        
        document.removeEventListener('keydown', handleKeyDown);
      };
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    }
  }, [lightboxPhoto]);

  if (photos.length === 0) {
    return (
      <div className="text-center py-12">
        <Eye className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune photo disponible</h3>
        <p className="mt-1 text-sm text-gray-500">
          Il n'y a pas encore de photos à afficher pour cet élève.
        </p>
      </div>
    );
  }

  const handlePhotoClick = (photo: Photo) => {
    setLightboxPhoto(photo);
  };

  const closeLightbox = () => {
    setLightboxPhoto(null);
  };

  const nextPhoto = () => {
    if (!lightboxPhoto) return;
    const currentIndex = photos.findIndex(p => p.id === lightboxPhoto.id);
    const nextIndex = (currentIndex + 1) % photos.length;
    setLightboxPhoto(photos[nextIndex]);
  };

  const prevPhoto = () => {
    if (!lightboxPhoto) return;
    const currentIndex = photos.findIndex(p => p.id === lightboxPhoto.id);
    const prevIndex = currentIndex === 0 ? photos.length - 1 : currentIndex - 1;
    setLightboxPhoto(photos[prevIndex]);
  };

  const allSelected = photos.length > 0 && photos.every(photo => selectedPhotos.includes(photo.id));

  return (
    <div className="space-y-6">
      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="flex items-center space-x-4">
          <button
            onClick={onSelectAll}
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            {allSelected ? 'Désélectionner tout' : 'Sélectionner tout'}
          </button>
          
          {selectedPhotos.length > 0 && (
            <span className="text-sm text-gray-600">
              {selectedPhotos.length} photo(s) sélectionnée(s)
            </span>
          )}
        </div>

        {selectedPhotos.length > 0 && (
          <button
            onClick={onDownload}
            disabled={isDownloading}
            className="btn-primary disabled:opacity-50"
          >
            <Download className="h-4 w-4 mr-2" />
            {isDownloading 
              ? 'Préparation...' 
              : `Télécharger (${selectedPhotos.length})`
            }
          </button>
        )}
      </div>

      {/* Photo Grid */}
      <div className="photo-grid">
        {photos.map((photo) => (
          <div key={photo.id} className="relative">
            <PhotoCard
              photo={photo}
              selected={selectedPhotos.includes(photo.id)}
              onClick={() => onPhotoSelect(photo)}
              selectable
              showTags={false}
            />
            
            {/* View button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlePhotoClick(photo);
              }}
              className="absolute top-2 left-2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-opacity z-10"
            >
              <Eye className="h-4 w-4" />
            </button>
            
            {/* Group photo indicator */}
            {photo.isGroupPhoto && (
              <div className="absolute bottom-2 left-2">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  <Grid className="h-3 w-3 mr-1" />
                  Groupe
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Précédent
          </button>
          
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700">
              Page {currentPage} sur {pagination.pages}
            </span>
            <span className="text-sm text-gray-500">
              ({pagination.total} photos)
            </span>
          </div>
          
          <button
            onClick={() => onPageChange(Math.min(pagination.pages, currentPage + 1))}
            disabled={currentPage === pagination.pages}
            className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Suivant
            <ChevronRight className="h-4 w-4 ml-2" />
          </button>
        </div>
      )}

      {/* Lightbox - Using portal to render directly to body */}
      {lightboxPhoto && createPortal(
        <div 
          onClick={closeLightbox}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            margin: 0,
            padding: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999,
            boxSizing: 'border-box'
          }}
        >
          {/* Close button - Fixed to top right of screen */}
          <button
            onClick={closeLightbox}
            style={{
              position: 'fixed',
              top: 24,
              right: 24,
              zIndex: 999999,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              padding: 8,
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            title="Fermer la lightbox"
            onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = 'rgba(0, 0, 0, 0.7)'}
            onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'rgba(0, 0, 0, 0.5)'}
          >
            <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Navigation buttons - Fixed to sides of screen */}
          {photos.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevPhoto();
                }}
                style={{
                  position: 'fixed',
                  left: 24,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 999999,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  padding: 12,
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                title="Photo précédente"
                onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = 'rgba(0, 0, 0, 0.7)'}
                onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'rgba(0, 0, 0, 0.5)'}
              >
                <ChevronLeft size={32} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextPhoto();
                }}
                style={{
                  position: 'fixed',
                  right: 24,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 999999,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  padding: 12,
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                title="Photo suivante"
                onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = 'rgba(0, 0, 0, 0.7)'}
                onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'rgba(0, 0, 0, 0.5)'}
              >
                <ChevronRight size={32} />
              </button>
            </>
          )}

          {/* Image container */}
          <div 
            style={{
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh',
              padding: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Image */}
            <img
              src={lightboxPhoto.originalUrl}
              alt={lightboxPhoto.originalName}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain'
              }}
            />

            {/* Photo info - Overlay on image */}
            <div style={{
              position: 'absolute',
              bottom: 16,
              left: 16,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              borderRadius: 4,
              padding: '8px 12px'
            }}>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 500 }}>{lightboxPhoto.originalName}</p>
              {lightboxPhoto.isGroupPhoto && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  marginTop: 8,
                  padding: '4px 8px',
                  borderRadius: 12,
                  fontSize: 12,
                  fontWeight: 500,
                  backgroundColor: '#2563eb',
                  color: 'white'
                }}>
                  <Grid size={12} style={{ marginRight: 4 }} />
                  Photo de groupe
                </span>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}